import { CreditCardPurchaseCommandService } from "@/lib/creditCardPurchaseCommandService";
import { CreditCardPaymentIntentService } from "@/lib/creditCardPaymentIntentService";
import {
  calculateCreditCardInvoiceSummary,
  type CreditCardInvoicePaymentV2,
  type CreditCardInvoiceSummaryV2,
  type CreditCardInvoiceV2,
} from "@/lib/creditCardDomainV2";
import {
  calculateInvoiceDates,
  calculateInvoiceMonth,
  deleteCreditCardPurchase,
  getInstallmentAmounts,
  getInvoiceCorrectionBalance,
  getInvoiceFinancialState,
  getInvoicePaidAmount,
  getResolvedInvoiceStatus,
  isInvoicePaidStatus,
  isInvoicePayableStatus,
  isInvoiceReopenableStatus,
  moveCreditCardPurchaseToInvoice,
  shiftMonth,
  updateCreditCardPurchase,
} from "@/lib/creditCardEngine";
import {
  buildCreditCardCommandIdempotencyKey,
  CreditCardCommandService,
  type CancelInvoicePaymentInput,
  type ReverseInvoicePaymentInput,
} from "@/lib/creditCardCommandService";
import {
  CREDIT_CARD_PURCHASE_SAVED_EVENT,
  createCreditCardPurchaseWithInvoices,
  repairCreditCardInvoices,
  syncCreditCardInvoices,
  type CreditCardInvoiceRow,
  type CreditCardPurchaseRow,
} from "@/lib/creditCardPurchases";
import { normalizeError } from "@/lib/errorUtils";
import { supabase } from "@/lib/supabase";

export type CreditCardApplicationCard = {
  id: string;
  user_id: string;
  name: string;
  bank_name: string;
  brand?: string | null;
  limit_amount?: number | string | null;
  closing_day: number | string;
  due_day: number | string;
  color?: string | null;
  image_url?: string | null;
  is_archived?: boolean | null;
  created_at?: string;
};

export type CreditCardApplicationAccount = {
  id: string;
  user_id?: string;
  name: string;
  institution?: string | null;
  current_balance: number | string;
  color?: string | null;
};

export type CreditCardApplicationInvoice = CreditCardInvoiceRow & {
  domain_v2: CreditCardInvoiceSummaryV2;
  resolved_status_v2: CreditCardInvoiceSummaryV2["status"];
  outstanding_balance: number;
  credit_balance: number;
  payment_history: CreditCardInvoicePaymentV2[];
};

export type CreditCardInvoicePrimaryAction =
  | "pay"
  | "continue_payment"
  | "reopen"
  | "view";

type CreditCardInvoiceActionInput = {
  status: CreditCardInvoiceRow["status"];
  paid_at: string | null;
  total_amount: number | string | null;
  domain_v2?: Pick<
    CreditCardInvoiceSummaryV2,
    "totalCharged" | "paidAmount" | "outstandingBalance" | "creditBalance" | "status"
  > | null;
};

export type CreditCardApplicationParityMismatch = {
  invoiceId: string;
  field: "total" | "status" | "paidAmount" | "dueDate";
  legacy: string | number | null;
  application: string | number | null;
};

export type CreditCardApplicationWorkspace = {
  cards: CreditCardApplicationCard[];
  purchases: CreditCardPurchaseRow[];
  invoices: CreditCardApplicationInvoice[];
  accounts: CreditCardApplicationAccount[];
  parity: {
    matches: boolean;
    mismatches: CreditCardApplicationParityMismatch[];
  };
};

type PaymentRow = {
  id: string;
  invoice_id: string;
  amount: number | string;
  payment_date: string;
  account_id: string | null;
  effective_at: string | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  status: CreditCardInvoicePaymentV2["status"];
  origin: CreditCardInvoicePaymentV2["origin"];
  transaction_id: string | null;
};

function mapPayment(row: PaymentRow): CreditCardInvoicePaymentV2 {
  return {
    id: row.id,
    invoiceId: row.invoice_id,
    amount: row.amount,
    date: row.payment_date,
    accountId: row.account_id,
    effectiveDate: row.effective_at,
    notes: row.notes,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    status: row.status,
    origin: row.origin,
    transactionId: row.transaction_id,
  };
}

function toInvoiceV2(invoice: CreditCardInvoiceRow): CreditCardInvoiceV2 {
  return {
    id: invoice.id,
    totalAmount: invoice.total_amount,
    status: invoice.status,
    closingDate: invoice.closing_date,
    dueDate: invoice.due_date,
    paidAt: invoice.paid_at,
    paidAmount: invoice.paid_amount,
    paidFromAccountId: invoice.paid_from_account_id,
    paidTransactionId: invoice.paid_transaction_id,
    userId: invoice.user_id,
    createdAt: invoice.created_at,
    updatedAt: invoice.updated_at,
  };
}

function money(value: number | string | null | undefined) {
  return Number(Number(value ?? 0).toFixed(2));
}

function legacyComparableStatus(
  invoice: CreditCardInvoiceRow,
  card: CreditCardApplicationCard | undefined,
  referenceDate?: string
) {
  return getResolvedInvoiceStatus(invoice, card, referenceDate);
}

function applicationComparableStatus(
  summary: CreditCardInvoiceSummaryV2,
  legacyStatus: string
) {
  return summary.status === "partially_paid" ? legacyStatus : summary.status;
}

export function composeCreditCardApplicationWorkspace({
  cards,
  purchases,
  invoices,
  payments,
  accounts = [],
  referenceDate,
  itemsComplete = true,
}: {
  cards: CreditCardApplicationCard[];
  purchases: CreditCardPurchaseRow[];
  invoices: CreditCardInvoiceRow[];
  payments: CreditCardInvoicePaymentV2[];
  accounts?: CreditCardApplicationAccount[];
  referenceDate?: string;
  itemsComplete?: boolean;
}): CreditCardApplicationWorkspace {
  const cardsById = new Map(cards.map(card => [card.id, card]));
  const purchasesByInvoice = new Map<string, CreditCardPurchaseRow[]>();
  const unlinkedPurchasesByCardMonth = new Map<string, CreditCardPurchaseRow[]>();
  const paymentsByInvoice = new Map<string, CreditCardInvoicePaymentV2[]>();

  for (const purchase of purchases) {
    if (purchase.invoice_id) {
      purchasesByInvoice.set(purchase.invoice_id, [
        ...(purchasesByInvoice.get(purchase.invoice_id) ?? []),
        purchase,
      ]);
    } else {
      const cardMonthKey = `${purchase.card_id}:${purchase.invoice_month}`;
      unlinkedPurchasesByCardMonth.set(cardMonthKey, [
        ...(unlinkedPurchasesByCardMonth.get(cardMonthKey) ?? []),
        purchase,
      ]);
    }
  }
  for (const payment of payments) {
    paymentsByInvoice.set(payment.invoiceId, [
      ...(paymentsByInvoice.get(payment.invoiceId) ?? []),
      payment,
    ]);
  }

  const mismatches: CreditCardApplicationParityMismatch[] = [];
  const applicationInvoices = invoices.map<CreditCardApplicationInvoice>(
    invoice => {
      const summary = calculateCreditCardInvoiceSummary({
        invoice: toInvoiceV2(invoice),
        items: itemsComplete
          ? [
              ...(purchasesByInvoice.get(invoice.id) ?? []),
              ...(unlinkedPurchasesByCardMonth.get(
                `${invoice.card_id}:${invoice.invoice_month}`
              ) ?? []),
            ].map(item => ({
              id: item.id,
              amount: item.amount,
              type: item.transaction_type,
              status: item.status,
            }))
          : [],
        payments: paymentsByInvoice.get(invoice.id) ?? [],
        referenceDate,
      });
      const legacyStatus = legacyComparableStatus(
        invoice,
        cardsById.get(invoice.card_id),
        referenceDate
      );
      const comparableStatus = applicationComparableStatus(summary, legacyStatus);
      const legacyPaidAmount = isInvoicePaidStatus(invoice.status)
        ? money(invoice.paid_amount ?? invoice.total_amount)
        : 0;

      const comparisons = [
        ["total", money(invoice.total_amount), summary.totalCharged],
        ["status", legacyStatus, comparableStatus],
        ["paidAmount", legacyPaidAmount, summary.paidAmount],
        ["dueDate", invoice.due_date, summary.nextDueDate ?? invoice.due_date],
      ] as const;

      for (const [field, legacy, application] of comparisons) {
        if (legacy !== application) {
          mismatches.push({
            invoiceId: invoice.id,
            field,
            legacy,
            application,
          });
        }
      }

      return {
        ...invoice,
        total_amount: summary.totalCharged,
        paid_amount: summary.paidAmount,
        status:
          summary.status === "partially_paid"
            ? invoice.status
            : summary.status,
        domain_v2: summary,
        resolved_status_v2: summary.status,
        outstanding_balance: summary.outstandingBalance,
        credit_balance: summary.creditBalance,
        payment_history: summary.paymentHistory,
      };
    }
  );

  return {
    cards,
    purchases,
    invoices: applicationInvoices,
    accounts,
    parity: {
      matches: mismatches.length === 0,
      mismatches,
    },
  };
}

async function loadWorkspace({
  userId,
  includeArchived = false,
  includeAccounts = false,
  purchaseLimit,
  invoiceLimit,
  referenceDate,
}: {
  userId: string;
  includeArchived?: boolean;
  includeAccounts?: boolean;
  purchaseLimit?: number;
  invoiceLimit?: number;
  referenceDate?: string;
}) {
  let cardsQuery = supabase
    .from("personal_credit_cards")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (!includeArchived) cardsQuery = cardsQuery.eq("is_archived", false);

  let purchasesQuery = supabase
    .from("personal_credit_card_purchases")
    .select("*")
    .eq("user_id", userId)
    .order("purchase_date", { ascending: false });
  if (purchaseLimit) purchasesQuery = purchasesQuery.limit(purchaseLimit);

  let invoicesQuery = supabase
    .from("personal_credit_card_invoices")
    .select("*")
    .eq("user_id", userId)
    .order("invoice_month", { ascending: false });
  if (invoiceLimit) invoicesQuery = invoicesQuery.limit(invoiceLimit);

  const accountsQuery = includeAccounts
    ? supabase
        .from("personal_accounts")
        .select("id, user_id, name, institution, current_balance, color")
        .eq("user_id", userId)
        .eq("is_archived", false)
    : Promise.resolve({ data: [], error: null });

  const [cardsResult, purchasesResult, invoicesResult, paymentsResult, accountsResult] =
    await Promise.all([
      cardsQuery,
      purchasesQuery,
      invoicesQuery,
      supabase
        .from("personal_credit_card_invoice_payments")
        .select(
          "id, invoice_id, amount, payment_date, account_id, effective_at, notes, created_by, created_at, updated_at, status, origin, transaction_id"
        )
        .eq("user_id", userId)
        .order("effective_at", { ascending: true })
        .order("created_at", { ascending: true }),
      accountsQuery,
    ]);

  const error =
    cardsResult.error ??
    purchasesResult.error ??
    invoicesResult.error ??
    paymentsResult.error ??
    accountsResult.error;
  if (error) {
    throw normalizeError(error, "credit_card_application_read_failed");
  }

  return composeCreditCardApplicationWorkspace({
    cards: (cardsResult.data ?? []) as CreditCardApplicationCard[],
    purchases: (purchasesResult.data ?? []) as CreditCardPurchaseRow[],
    invoices: (invoicesResult.data ?? []) as CreditCardInvoiceRow[],
    payments: ((paymentsResult.data ?? []) as PaymentRow[]).map(mapPayment),
    accounts: (accountsResult.data ?? []) as CreditCardApplicationAccount[],
    referenceDate,
    itemsComplete: !purchaseLimit,
  });
}

async function loadCardOptions(userId: string) {
  const { data, error } = await supabase
    .from("personal_credit_cards")
    .select("id, name, bank_name, closing_day, due_day, is_archived")
    .eq("user_id", userId)
    .eq("is_archived", false)
    .order("created_at", { ascending: true });
  if (error) throw normalizeError(error, "credit_card_options_read_failed");
  return (data ?? []) as CreditCardApplicationCard[];
}

async function saveCard({
  userId,
  cardId,
  payload,
}: {
  userId: string;
  cardId?: string | null;
  payload: Record<string, unknown>;
}) {
  const result = cardId
    ? await supabase
        .from("personal_credit_cards")
        .update(payload)
        .eq("id", cardId)
        .eq("user_id", userId)
        .eq("is_archived", false)
        .select("*")
        .single()
    : await supabase
        .from("personal_credit_cards")
        .insert({
          user_id: userId,
          ...payload,
          image_url: payload.image_url ?? null,
          is_archived: false,
        })
        .select("*")
        .single();
  if (result.error) throw normalizeError(result.error, "credit_card_save_failed");
  return result.data as CreditCardApplicationCard;
}

async function getCardUsageCounts(userId: string, cardId: string) {
  const [purchaseResult, invoiceResult] = await Promise.all([
    supabase
      .from("personal_credit_card_purchases")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("card_id", cardId),
    supabase
      .from("personal_credit_card_invoices")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("card_id", cardId),
  ]);
  const error = purchaseResult.error ?? invoiceResult.error;
  if (error) throw normalizeError(error, "credit_card_usage_read_failed");
  return {
    purchaseCount: purchaseResult.count ?? 0,
    invoiceCount: invoiceResult.count ?? 0,
  };
}

async function archiveOrDeleteCard({
  userId,
  cardId,
  action,
}: {
  userId: string;
  cardId: string;
  action: "archive" | "delete";
}) {
  if (action === "delete") {
    await CreditCardPurchaseCommandService.mutate({ userId, cardId, operation: "delete_card" });
    return;
  }
  const result = await supabase
    .from("personal_credit_cards")
    .update({ is_archived: true })
    .eq("id", cardId)
    .eq("user_id", userId);
  if (result.error) {
    throw normalizeError(result.error, "credit_card_lifecycle_update_failed");
  }
}

async function deleteCardWithPurchases({
  userId, cardId,
}: {
  userId: string;
  cardId: string;
  hasPurchases: boolean;
}) {
  await CreditCardPurchaseCommandService.mutate({ userId, cardId, operation: "delete_card" });
}

async function countInvoicesPaidFromAccount(accountId: string) {
  return supabase
    .from("personal_credit_card_invoices")
    .select("id", { count: "exact", head: true })
    .eq("paid_from_account_id", accountId);
}

type PayInvoiceOptions = {
  userId: string;
  invoice: CreditCardApplicationInvoice | CreditCardInvoiceRow;
  cardName: string;
  account?: CreditCardApplicationAccount | null;
  amount?: number;
  transactionDate?: string;
};

export type CreditCardPaymentPreview = {
  paymentAmount: number;
  totalAmount: number;
  paidAmount: number;
  paidAfterPayment: number;
  outstandingAfterPayment: number;
  creditAfterPayment: number;
  outcome: "partial" | "paid" | "credit";
};

type ReopenInvoiceOptions = {
  userId: string;
  invoice: CreditCardApplicationInvoice | CreditCardInvoiceRow;
  card: CreditCardApplicationCard;
};

function getInvoicePaymentAmounts(
  invoice: CreditCardApplicationInvoice | CreditCardInvoiceRow
) {
  if ("domain_v2" in invoice) {
    return {
      totalAmount: money(invoice.domain_v2.totalCharged),
      paidAmount: money(invoice.domain_v2.paidAmount),
      outstandingBalance: money(invoice.domain_v2.outstandingBalance),
    };
  }

  const totalAmount = money(invoice.total_amount);
  const paidAmount = money(invoice.paid_amount);
  return {
    totalAmount,
    paidAmount,
    outstandingBalance: money(Math.max(0, totalAmount - paidAmount)),
  };
}

export function previewCreditCardInvoicePayment(
  invoice: CreditCardApplicationInvoice | CreditCardInvoiceRow,
  amount: number
): CreditCardPaymentPreview {
  const paymentAmount = money(amount);
  if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
    throw new Error("credit_card_payment_amount_must_be_positive");
  }

  const { totalAmount, paidAmount } = getInvoicePaymentAmounts(invoice);
  const paidAfterPayment = money(paidAmount + paymentAmount);
  const outstandingAfterPayment = money(
    Math.max(0, totalAmount - paidAfterPayment)
  );
  const creditAfterPayment = money(
    Math.max(0, paidAfterPayment - totalAmount)
  );

  return {
    paymentAmount,
    totalAmount,
    paidAmount,
    paidAfterPayment,
    outstandingAfterPayment,
    creditAfterPayment,
    outcome:
      creditAfterPayment > 0
        ? "credit"
        : outstandingAfterPayment > 0
          ? "partial"
          : "paid",
  };
}

export function resolveCreditCardInvoicePrimaryAction(
  invoice: CreditCardInvoiceActionInput | null | undefined
): CreditCardInvoicePrimaryAction {
  if (!invoice) return "view";

  const totalAmount = money(
    invoice.domain_v2?.totalCharged ?? invoice.total_amount
  );
  const paidAmount = money(
    invoice.domain_v2?.paidAmount ??
      (isInvoicePaidStatus(invoice.status) ? totalAmount : 0)
  );
  const outstandingBalance = money(
    invoice.domain_v2?.outstandingBalance ??
      Math.max(0, totalAmount - paidAmount)
  );

  if (totalAmount <= 0) return "view";
  if (outstandingBalance > 0) {
    return paidAmount > 0 ? "continue_payment" : "pay";
  }
  if (
    invoice.domain_v2?.status === "paid" &&
    isInvoiceReopenableStatus(invoice)
  ) {
    return "reopen";
  }

  return "view";
}

async function payInvoice({
  userId,
  invoice,
  account,
  amount,
  transactionDate = new Date().toISOString().slice(0, 10),
}: PayInvoiceOptions) {
  const paymentAmount =
    amount ?? getInvoicePaymentAmounts(invoice).outstandingBalance;
  const preview = previewCreditCardInvoicePayment(invoice, paymentAmount);

  const result = await CreditCardPaymentIntentService.register(userId, {
    invoiceId: invoice.id,
    amount: preview.paymentAmount,
    accountId: account?.id ?? null,
    paymentDate: transactionDate,
  });

  return {
    invoice: result.invoice,
    payment: result.payment,
    paidAt: result.payment.effective_at,
    amount: Number(result.payment.amount),
    paidFromAccountId: result.payment.account_id,
    paidTransactionId: result.transaction_id,
    nextAccountBalance:
      result.account === null ? null : Number(result.account.current_balance),
  };
}

async function reopenInvoice({ invoice }: ReopenInvoiceOptions) {
  const reopenedAt = new Date().toISOString();
  const result = await CreditCardCommandService.reopenInvoice({
    invoiceId: invoice.id,
    reopenedAt,
    idempotencyKey: buildCreditCardCommandIdempotencyKey("reopen", [
      invoice.id,
      invoice.paid_at,
      invoice.paid_amount ?? invoice.total_amount,
      invoice.paid_transaction_id,
    ]),
  });
  const restoredAccount = result.accounts[0] ?? null;

  return {
    invoice: result.invoice,
    nextStatus: result.invoice.status,
    restoredAccountId: restoredAccount?.id ?? null,
    restoredAccountBalance:
      restoredAccount === null ? null : Number(restoredAccount.current_balance),
  };
}

function reverseInvoicePayment(input: ReverseInvoicePaymentInput) {
  return CreditCardCommandService.reverseInvoicePayment(input);
}

function cancelInvoicePayment(input: CancelInvoicePaymentInput) {
  return CreditCardCommandService.cancelInvoicePayment(input);
}

export const CreditCardApplicationService = {
  getTransferBlockReason: getCreditCardTransferBlockReason,
  loadWorkspace,
  loadCardOptions,
  saveCard,
  getCardUsageCounts,
  archiveOrDeleteCard,
  deleteCardWithPurchases,
  countInvoicesPaidFromAccount,
  previewInvoicePayment: previewCreditCardInvoicePayment,
  resolveInvoicePrimaryAction: resolveCreditCardInvoicePrimaryAction,
  calculateInvoiceDates,
  calculateInvoiceMonth,
  getInstallmentAmounts,
  getInvoiceCorrectionBalance,
  getInvoiceFinancialState,
  getInvoicePaidAmount,
  getResolvedInvoiceStatus,
  isInvoicePaidStatus,
  isInvoicePayableStatus,
  isInvoiceReopenableStatus,
  shiftMonth,
  syncInvoices: syncCreditCardInvoices,
  createPurchase: createCreditCardPurchaseWithInvoices,
  repairInvoices: repairCreditCardInvoices,
  updatePurchase: updateCreditCardPurchase,
  movePurchase: moveCreditCardPurchaseToInvoice,
  deletePurchase: deleteCreditCardPurchase,
  payInvoice,
  reverseInvoicePayment,
  cancelInvoicePayment,
  reopenInvoice,
} as const;

export function getCreditCardTransferBlockReason(
  purchase: CreditCardPurchaseRow,
  purchases: CreditCardPurchaseRow[],
  invoices: Array<CreditCardInvoiceRow & { payment_history?: Array<{ status: string }> }>,
  targetCardId = purchase.card_id,
  targetMonth = purchase.invoice_month
): string | null {
  if (Number(purchase.installments ?? 1) !== 1 ||
    Number(purchase.current_installment ?? 1) !== 1 || purchase.installment_group_id ||
    (purchase.transaction_type ?? "purchase") !== "purchase" || purchase.original_purchase_id ||
    Number(purchase.amount) <= 0 || purchase.status === "cancelled" ||
    purchases.some(item => item.original_purchase_id === purchase.id)) {
    return "Transferência disponível apenas para compras simples, sem parcelas, repetições ou estornos relacionados.";
  }
  if (purchase.status === "paid" || purchase.paid_at || purchase.paid_from_account_id ||
    invoices.some(invoice =>
      ((invoice.card_id === purchase.card_id && invoice.invoice_month === purchase.invoice_month) ||
        (invoice.card_id === targetCardId && invoice.invoice_month === targetMonth)) &&
      (invoice.status === "paid" || Number(invoice.paid_amount ?? 0) !== 0 ||
        invoice.paid_at || invoice.paid_from_account_id || invoice.paid_transaction_id ||
        invoice.payment_history?.some(payment => payment.status === "completed")))) {
    return "Transferência indisponível: a fatura de origem ou destino possui pagamento associado.";
  }
  return null;
}

export { CREDIT_CARD_PURCHASE_SAVED_EVENT };
